import React, { useEffect, useRef, useState } from "react";
import { Modal, ModalDialog, CircularProgress } from "@mui/joy";
import { Close, DescriptionOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFetch, postFetch, postFetchFormData } from "../../../../fetch/fetch";
import styles from "./faqadmin.module.css";
import { downloadBase64Xlsx, formatNumber, toastOptions } from "./faqutil";

/**
 * 엑셀로 한번에 추가 (목업 ImportModal, 3단계)
 *  ① 파일 선택(.xlsx, '양식 받기') → POST /api/faq/items/import/preview (multipart) → 서버가 행별 추가/중복/오류 판정 + importToken(Redis 30분)
 *  ② 미리보기: 요약 칩 + 행별 표 (중복=노란 배경+기존 #id, 오류=빨간 배경+사유) → [N건 추가하기] → POST /import/commit { importToken }
 *  ③ 완료: 추가 N / 건너뜀 N. 추가된 항목은 '미배포' 상태 → 배포하기를 눌러야 직원 화면에 나감
 */
const FaqImportModal : React.FC<any> = (props : any) => {

    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState<any>(null);
    const [result, setResult] = useState<any>(null);
    const fileRef = useRef<any>(null);

    useEffect(() => {
        if (props.open) {
            setStep(1);
            setPreview(null);
            setResult(null);
            setBusy(false);
        }
    }, [props.open]);

    const downloadTemplate = async () => {
        try {
            const r : any = await getFetch("/api/faq/items/import/template");
            if (r && r.message === "success") {
                downloadBase64Xlsx(r.fileName, r.base64);
            } else {
                toast.error("양식을 받지 못했습니다", toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("양식을 받지 못했습니다", toastOptions);
        }
    };

    const onFile = async (file : File | null) => {
        if (!file) {
            return;
        }
        if (!/\.xlsx$/i.test(file.name)) {
            toast.warning(".xlsx 파일만 올릴 수 있습니다", toastOptions);
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r : any = await postFetchFormData("/api/faq/items/import/preview", fd);
            if (r && r.message === "success") {
                setPreview(r);
                setStep(2);
            } else if (r && r.message === "NO_HEADER") {
                toast.error("헤더 행을 찾지 못했습니다. '카테고리'·'질문' 컬럼이 있는 양식인지 확인해 주세요", { ...toastOptions, autoClose : 6000 });
            } else {
                toast.error("엑셀을 읽지 못했습니다", toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("업로드에 실패했습니다", toastOptions);
        } finally {
            setBusy(false);
            if (fileRef.current) {
                fileRef.current.value = "";
            }
        }
    };

    const commit = async () => {
        if (!preview) {
            return;
        }
        setBusy(true);
        try {
            const r : any = await postFetch("/api/faq/items/import/commit", { importToken : preview.importToken });
            if (r && r.message === "success") {
                setResult(r);
                setStep(3);
                toast.success(`${formatNumber(r.inserted)}건 추가됨 (배포 전까지 미반영)`, toastOptions);
            } else if (r && r.message === "EXPIRED") {
                toast.error("미리보기가 만료되었습니다(30분). 파일을 다시 올려 주세요", toastOptions);
                setStep(1);
            } else {
                toast.error("추가에 실패했습니다", toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("추가에 실패했습니다", toastOptions);
        } finally {
            setBusy(false);
        }
    };

    const addCount = preview ? +preview.summary.add : 0;

    return (
        <Modal open={props.open} onClose={() => { if (!busy) props.onClose(); }}>
            <ModalDialog sx={{ padding : 0, borderRadius : "14px", width : "800px", maxWidth : "95vw" }}>
                <div className={styles.modal} style={{ boxShadow : "none" }}>
                    <div className={styles.modalHeader}>
                        엑셀로 한번에 추가
                        <button className={styles.closeBtn} aria-label="닫기" onClick={props.onClose} disabled={busy}><Close sx={{ fontSize : 20 }} /></button>
                    </div>
                    <div className={styles.modalBody}>
                        <div className={styles.stepper}>
                            <div className={`${styles.step} ${step === 1 ? styles.stepCur : ""}`}><span className={`${styles.stepNo} ${step >= 1 ? styles.done : ""}`}>1</span> 파일 선택</div>
                            <div className={styles.stepLine} />
                            <div className={`${styles.step} ${step === 2 ? styles.stepCur : ""}`}><span className={`${styles.stepNo} ${step >= 2 ? styles.done : ""}`}>2</span> 미리보기·확인</div>
                            <div className={styles.stepLine} />
                            <div className={`${styles.step} ${step === 3 ? styles.stepCur : ""}`}><span className={`${styles.stepNo} ${step >= 3 ? styles.done : ""}`}>3</span> 추가 완료</div>
                        </div>

                        <input ref={fileRef} type="file" accept=".xlsx" style={{ display : "none" }} onChange={(e) => onFile(e.target.files && e.target.files.length > 0 ? e.target.files[0] : null)} />

                        {
                            step === 1 ?
                                <>
                                    <div className={styles.dropZone} onClick={() => { if (!busy && fileRef.current) fileRef.current.click(); }}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => { e.preventDefault(); if (!busy) onFile(e.dataTransfer.files && e.dataTransfer.files.length > 0 ? e.dataTransfer.files[0] : null); }}>
                                        {busy ? <div className={styles.rowFlex} style={{ justifyContent : "center" }}><CircularProgress size="sm" /> 파일을 읽고 판정하는 중…</div> : <>여기를 눌러 .xlsx 파일을 선택하거나 끌어다 놓으세요</>}
                                    </div>
                                    <div className={styles.rowFlex} style={{ justifyContent : "space-between" }}>
                                        <div className={styles.helper}>양식 컬럼: 카테고리 · 세부분류 · 질문 · 최종 답변 · (선택) 비고 · 출처. 첫 시트의 헤더 행을 자동으로 찾습니다.</div>
                                        <button className={styles.linkBtn} onClick={downloadTemplate}>양식 받기</button>
                                    </div>
                                </>
                                : null
                        }

                        {
                            step === 2 && preview ?
                                <>
                                    <div className={styles.fileRow}>
                                        <DescriptionOutlined sx={{ color : "#15803D" }} />
                                        <span><b>{preview.fileName}</b> <span className="sub" style={{ color : "#6B7280" }}>· 시트 "{preview.sheetName}" · {formatNumber(preview.rowCount)}행</span></span>
                                        <button className={`${styles.btn} ${styles.btnSm}`} style={{ marginLeft : "auto", height : "34px" }} disabled={busy} onClick={() => { if (fileRef.current) fileRef.current.click(); }}>다른 파일</button>
                                        <button className={styles.linkBtn} onClick={downloadTemplate}>양식 받기</button>
                                    </div>
                                    {
                                        preview.missingColumns && preview.missingColumns.length > 0 ?
                                            <div className={`${styles.chip} ${styles.badgeRed}`} style={{ alignSelf : "flex-start" }}>누락 컬럼: {preview.missingColumns.join(", ")}</div>
                                            : null
                                    }
                                    <div className={styles.chips}>
                                        <span className={`${styles.chip} ${styles.badgeGreen}`}>추가 {formatNumber(preview.summary.add)}건</span>
                                        <span className={`${styles.chip} ${styles.badgeAmber}`}>같은 질문 있음 {formatNumber(preview.summary.dup)}건 → 건너뜀</span>
                                        <span className={`${styles.chip} ${styles.badgeRed}`}>오류 {formatNumber(preview.summary.error)}건</span>
                                        {preview.newCategories.length > 0 ? <span className={`${styles.chip} ${styles.badgeInfo}`}>새 카테고리 {preview.newCategories.length}개: {preview.newCategories.join(", ")}</span> : null}
                                    </div>
                                    <div className={styles.previewTable}>
                                        <table>
                                            <thead>
                                                <tr><th style={{ width : 40 }}>행</th><th style={{ width : 60 }}>결과</th><th style={{ width : 120 }}>카테고리</th><th style={{ width : 130 }}>세부분류</th><th>질문</th><th>답변</th></tr>
                                            </thead>
                                            <tbody>
                                                {preview.rows.map((r : any) => (
                                                    <tr key={r.row} className={r.status === "dup" ? styles.rowDup : r.status === "error" ? styles.rowErr : ""}>
                                                        <td className={styles.cellMuted}>{r.row}</td>
                                                        <td style={{ fontWeight : 700, color : r.status === "add" ? "#166534" : r.status === "dup" ? "#92400E" : "#991B1B" }}>{r.status === "add" ? "추가" : r.status === "dup" ? "중복" : "오류"}</td>
                                                        <td>{r.category}{r.newCategory ? <span style={{ color : "#3E4DB4", fontSize : "11px", marginLeft : "4px" }}>(신규)</span> : null}</td>
                                                        <td className={styles.cellSub}>{r.subcategory}</td>
                                                        <td><div className={styles.ellipsis} title={r.question}>{r.question}</div></td>
                                                        <td>
                                                            {
                                                                r.status === "add" ? <div className={styles.ellipsis} style={{ color : "#4B5563" }} title={r.answer}>{r.answer}</div>
                                                                : r.status === "dup" ? <span style={{ color : "#92400E" }}>{r.reason} → 건너뜀</span>
                                                                : <span style={{ color : "#B91C1C" }}>{r.reason}</span>
                                                            }
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className={styles.helper}>중복 판정은 질문의 공백·끝 물음표를 제거한 뒤 기존 활성 FAQ 와 완전 일치하는 경우입니다. 추가된 항목은 "미배포" 상태로 들어가고, 배포하기를 눌러야 직원 화면에 나갑니다.</div>
                                </>
                                : null
                        }

                        {
                            step === 3 && result ?
                                <div style={{ fontSize : "14px", lineHeight : 1.8, padding : "12px 0" }}>
                                    <b>{formatNumber(result.inserted)}건</b> 추가됨{result.skipped > 0 ? ` · ${formatNumber(result.skipped)}건은 커밋 시점에 중복으로 건너뜀` : ""}.
                                    <div className={styles.helper}>추가된 항목은 미배포 상태입니다. 상단 [배포하기]를 눌러야 직원 챗에 반영됩니다.</div>
                                </div>
                                : null
                        }
                    </div>
                    <div className={styles.modalFooter}>
                        {
                            step === 2 ?
                                <>
                                    <button className={styles.btn} disabled={busy} onClick={props.onClose}>취소</button>
                                    <button className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy || addCount === 0} onClick={commit}>{busy ? "추가 중…" : `${formatNumber(addCount)}건 추가하기`}</button>
                                </>
                                : step === 3 ?
                                    <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={props.onCommitted}>닫기</button>
                                    :
                                    <button className={styles.btn} disabled={busy} onClick={props.onClose}>취소</button>
                        }
                    </div>
                </div>
            </ModalDialog>
        </Modal>
    );
};

export default FaqImportModal;
